/**
 * View-mode body for a comment card (#2244). Renders the comment
 * text plus resolve / delete / edit affordances. Split from
 * `comment-card.tsx` so no file exceeds the 300-LOC limit.
 */

import { useTranslation } from 'react-i18next';
import { CheckCircle2, CircleDashed, PencilLine, Trash2 } from 'lucide-react';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';

export interface CommentCardViewProps {
  text: string;
  resolved: boolean;
  readOnly: boolean;
  onEnterEdit: () => void;
  onConfirmDelete: () => void;
  onToggleResolved?: () => Promise<void> | void;
  hasDelete: boolean;
}

const ICON_BUTTON =
  'inline-flex h-7 w-7 items-center justify-center rounded-2xl cursor-pointer hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary';

export function CommentCardView({
  text,
  resolved,
  readOnly,
  onEnterEdit,
  onConfirmDelete,
  onToggleResolved,
  hasDelete
}: CommentCardViewProps) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  return (
    <>
      <p
        className="text-xs leading-relaxed whitespace-pre-wrap mt-1"
        style={{ color: getColour('normalText') }}
      >
        {text}
      </p>
      {!readOnly && (
        <div className="mt-0.5 flex items-center justify-end gap-0.5">
          {onToggleResolved && (
            <button
              type="button"
              onClick={() => onToggleResolved()}
              aria-label={resolved ? t('unresolve') : t('resolve')}
              title={resolved ? t('unresolve') : t('resolve')}
              className={ICON_BUTTON}
              style={{ color: getColour('icon') }}
            >
              {resolved ? (
                <CircleDashed size={13} aria-hidden="true" />
              ) : (
                <CheckCircle2 size={13} aria-hidden="true" />
              )}
            </button>
          )}
          {hasDelete && (
            <button
              type="button"
              onClick={onConfirmDelete}
              aria-label={t('delete')}
              className={ICON_BUTTON}
              style={{ color: getColour('icon') }}
            >
              <Trash2 size={13} aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            onClick={onEnterEdit}
            aria-label={t('edit')}
            className={ICON_BUTTON}
            style={{ color: getColour('icon') }}
          >
            <PencilLine size={13} aria-hidden="true" />
          </button>
        </div>
      )}
    </>
  );
}
